import { useId, useState } from 'react';
import { AdminUser, PasswordReset, SentInvite, changeMyPassword } from '../api/admin';
import { Modal } from './fs';

/**
 * The two password dialogs, shared by every screen that holds a list of
 * people: the tenant's Users page, the platform staff roster, and the
 * console's per-account users dialog.
 *
 * They live together because the rules are the same wherever they open, and
 * three copies of "at least 8 characters" would be three chances to disagree
 * with the server about it.
 */

/**
 * The code's minimum — the server's default (MIN_PASSWORD there), and the
 * fallback here until the server has said its own. Stated, not merely
 * enforced.
 */
export const MIN_PASSWORD = 8;

/**
 * The minimum in force, as the server last said it (Platform › Rules’
 * `minPasswordLength`, carried on /me and on the public catalogue). Set by
 * the shells and the public pages as they load; read by every password
 * field's sentence, so raising the rule in the console raises the copy.
 */
let knownMin = MIN_PASSWORD;
export function setKnownMinPassword(n: number | null | undefined): void {
  if (typeof n === 'number' && Number.isInteger(n) && n >= MIN_PASSWORD) knownMin = n;
}
export const minPasswordLength = (): number => knownMin;

/**
 * A password field with its confirmation, and one line saying what is wrong.
 *
 * Confirmation rather than a reveal toggle: the failure this is guarding
 * against is a typo in a value nobody can read back, and typing it twice
 * catches that without putting a password on somebody's screen.
 */
export function PasswordFields({ value, confirm, onChange, autoFocus, label = 'New password' }: {
  value: string;
  confirm: string;
  onChange: (next: { value: string; confirm: string }) => void;
  autoFocus?: boolean;
  label?: string;
}) {
  // Two of these can be on screen in one flow, so the ids are generated
  // rather than written — a duplicate id points both labels at one field.
  const id = useId();
  return (
    <div className="fs-row-2">
      <div className="fs-field">
        <label htmlFor={`${id}-new`}>{label}</label>
        <input id={`${id}-new`} type="password" autoFocus={autoFocus} autoComplete="new-password"
          value={value} onChange={(e) => onChange({ value: e.target.value, confirm })} />
      </div>
      <div className="fs-field">
        <label htmlFor={`${id}-repeat`}>Repeat it</label>
        <input id={`${id}-repeat`} type="password" autoComplete="new-password" value={confirm}
          onChange={(e) => onChange({ value, confirm: e.target.value })} />
      </div>
    </div>
  );
}

/** Why this pair cannot be submitted yet, or null. Mirrors passwordRefusal. */
export function passwordPairError(value: string, confirm: string): string | null {
  const min = minPasswordLength();
  if (value.length < min) return `Password must be at least ${min} characters.`;
  if (value !== confirm) return 'The two passwords do not match.';
  return null;
}

/**
 * Reset somebody else's password.
 *
 * Where mail works there is exactly one way: email them a link. Nobody who
 * administers an account should ever be in a position to say a colleague's
 * password out loud, which is the same reason nothing creates a user with one
 * any more.
 *
 * Typing one stays reachable on an install with NO outgoing mail, because the
 * alternative there is an account nobody can help at all. It is a fallback for
 * a broken install, not a choice.
 */
export function ResetPasswordModal({ user, canEmail, onClose, onReset }: {
  user: AdminUser;
  /** false when this install has no SMTP — the link option is not offered. */
  canEmail: boolean;
  onClose: () => void;
  onReset: (body: PasswordReset) => void;
}) {
  // No mail, no choice: the dropdown that used to offer "set one now" is gone
  // wherever a link can be sent, so the mode follows the install.
  const mode: 'link' | 'set' = canEmail ? 'link' : 'set';
  const [pw, setPw] = useState({ value: '', confirm: '' });
  const [touched, setTouched] = useState(false);

  const who = user.name || user.email;
  const pairError = passwordPairError(pw.value, pw.confirm);
  const blocked = mode === 'set' && !!pairError;

  function submit() {
    setTouched(true);
    if (blocked) return;
    onReset(mode === 'link' ? { sendLink: true } : { password: pw.value });
  }

  return (
    <Modal title={`Reset password — ${who}`} onClose={onClose}
      footer={(
        <>
          <button className="fs-btn primary" onClick={submit}>
            {mode === 'link' ? 'Email a reset link' : 'Set this password'}
          </button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}>
      {mode === 'set' && (
        <div>
          <PasswordFields value={pw.value} confirm={pw.confirm} autoFocus onChange={setPw} />
          {touched && pairError && <div className="ad-err" style={{ marginTop: 10 }}>{pairError}</div>}
        </div>
      )}

      <div className="fs-consequence" style={{ marginTop: 16 }}>
        {mode === 'link' ? (
          <>
            <b>{user.email}</b> gets a link that works once and expires in an hour. Their current
            password keeps working until they use it, and you never learn the new one. Every other
            browser they are signed in on is signed out the moment they set it.
          </>
        ) : (
          <>
            <b>{who}</b> is signed out everywhere immediately and can only get back in with the
            password you type here — so you have to be able to reach them to tell them.
            {canEmail && ' They are emailed to say their password was changed, but not what it is.'}
          </>
        )}
      </div>
      {!canEmail && (
        <div className="fs-page-sub" style={{ marginTop: 10 }}>
          Emailing a link needs outgoing mail, which is not configured on this server yet.
        </div>
      )}
    </Modal>
  );
}

/**
 * Change your own.
 *
 * Asks for the current password, which is what separates changing your own
 * password from somebody finding an unlocked laptop. The tab doing the
 * changing stays signed in; the others do not.
 */
export function ChangePasswordModal({ email, onClose, onDone }: {
  email: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState({ value: '', confirm: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const currentId = useId();

  async function save() {
    const pairError = passwordPairError(pw.value, pw.confirm);
    if (!current) { setError('Enter your current password.'); return; }
    if (pairError) { setError(pairError); return; }
    setError(''); setBusy(true);
    try {
      await changeMyPassword(current, pw.value);
      onDone();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Change password" narrow onClose={onClose}
      footer={(
        <>
          <button className="fs-btn primary" disabled={busy} onClick={save}>
            {busy ? 'Saving…' : 'Change password'}
          </button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}>
      {error && <div className="ad-err">{error}</div>}
      <div className="fs-field">
        <label htmlFor={currentId}>Current password</label>
        <input id={currentId} type="password" autoFocus autoComplete="current-password" value={current}
          onChange={(e) => setCurrent(e.target.value)} />
      </div>
      <div style={{ marginTop: 14 }}>
        <PasswordFields value={pw.value} confirm={pw.confirm} onChange={setPw} />
      </div>
      <div className="fs-consequence" style={{ marginTop: 16 }}>
        You stay signed in here. Every other browser signed in as <b>{email}</b> is signed out, and
        any reset link still sitting in your inbox stops working.
      </div>
    </Modal>
  );
}

/**
 * The setup link, when we could not email it.
 *
 * Only ever shown for an install with no outgoing mail (or a mail server that
 * refused us). Handing the link to the admin does make them, for as long as it
 * takes to paste it, the holder of somebody else's credential — but it is
 * single use, it expires, and what the person ends up signing in with is
 * chosen by them and seen by nobody. That is strictly better than the password
 * this whole flow replaced. Where mail works, this never opens.
 */
export function InviteLinkModal({ invite, onClose }: { invite: SentInvite; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const url = invite.url || '';

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Clipboard permission is not a failure worth a red box — the link is
      // on screen and selectable, which is what actually matters.
      setCopied(false);
    }
  }

  return (
    <Modal title={`Send this link to ${invite.email}`} onClose={onClose}
      footer={<button className="fs-btn primary" onClick={onClose}>Done</button>}>
      <div className="ad-err">{invite.error || 'The invitation could not be emailed.'}</div>
      <div className="fs-field" style={{ marginTop: 14 }}>
        <label htmlFor="invite-url">Setup link</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input id="invite-url" type="text" readOnly value={url} style={{ flex: 1 }}
            onFocus={(e) => e.currentTarget.select()} />
          <button className="fs-btn neutral" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
        </div>
      </div>
      <div className="fs-consequence" style={{ marginTop: 16 }}>
        <b>{invite.email}</b> chooses their own password on this page — you will not know it. The
        link works once and expires in a week. Until they use it, the login exists but nobody can
        sign in to it.
      </div>
    </Modal>
  );
}

/** The one-line banner for an invitation that did go out. */
export const invitedMessage = (invite: SentInvite): string =>
  `Invited ${invite.email} — they have an email with a link to set their own password.`;
